import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { environment } from '@environments/environment';
import { RepositionDurationBodyInterface } from '@interfaces/reposition-duration-body.interface';
import { RepositionDurationResponse } from '@interfaces/reposition-duration-response.interface';
import { normalizeRepositionDurationResponse } from '../pages/itinerary/modules/reposition-duration-fields/helpers/get-reposition-duration-minutes.helper';
import { ILoggedInUserViewModel } from '../model/common.model';
import { IAddressDetailViewModel } from '../model/Location.model';
import { AssignedLegDetails } from '../model/report.model';
import { ILegDetailsConfig } from '../model/reusableConfigData.model';
import { IEditAssignmentDetailViewModel, IEditAssignmentPayload } from '@interfaces/assignment-details.interface';
import { IDriverLegRequestViewModel, UpdateStatusModel } from './driver.model';
import { LegPayload, LegTemplateResponseElement } from './itinerary.model';
import { LocationService } from './location.service';
import { Coords } from '@interfaces/coords.interface';

// todo: In the future, the service should be split into smaller parts, each responsible for a specific function.
// todo: We need to avoid large services that affect multiple components and are not suitable for testing.
@Injectable({
  providedIn: 'root',
})
export class ItineraryService {
  // todo need to refactor to remove $refreshLegDetails
  $refreshLegDetails: Subject<ILegDetailsConfig> = new Subject();

  constructor(
    private httpClient: HttpClient,
    private _locationService: LocationService,
  ) {}

  deleteLeg(legId): Observable<any> {
    const url = environment.apiHost + '/Leg/' + legId;

    return this.httpClient.delete(url).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  deleteLegs(legIds: string[]): Observable<boolean> {
    const url = environment.apiHost + '/Leg/deleteLegsBasedOnLegIds';

    return this.httpClient.post<boolean>(url, legIds).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getLegById(legId): Observable<LegPayload> {
    const url = environment.apiHost + '/Leg/' + legId;

    return this.httpClient.get<LegPayload>(url).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getLegsByIds(legIds: string[]): Observable<LegPayload[]> {
    const url = environment.apiHost + '/Leg/getLegListByLegIds';

    return this.httpClient.post<LegPayload[]>(url, legIds).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  addLeg(legPayload: LegPayload): Observable<any> {
    const url = environment.apiHost + '/Leg';

    return this.httpClient.post(url, legPayload).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  createMultipleLegs(legs: LegPayload[]): Observable<boolean> {
    const url = environment.apiHost + '/Leg/createMultipleLegs';

    return this.httpClient.post<boolean>(url, legs).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  assignLeg(assignLeg): Observable<any> {
    const url = environment.apiHost + '/Leg/assignLeg';

    return this.httpClient.post(url, assignLeg).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  saveLegTemplate(templatePayload): Observable<any> {
    const url = environment.apiHost + '/Leg/SaveTemplate';

    return this.httpClient.post(url, templatePayload).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getAllLegTemplates(): Observable<any> {
    const url = environment.apiHost + '/Leg/GetAllTemplate';

    return this.httpClient.get<LegTemplateResponseElement[]>(url).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  copyLeg(legId: string, serviceRequestId: string): Observable<boolean> {
    const url = environment.apiHost + '/Leg/CopyLeg/' + legId + '/' + serviceRequestId;

    return this.httpClient.get<boolean>(url).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  updateDriverStatus(status: UpdateStatusModel, isUpdatedBySystem: boolean): Observable<any> {
    const url = `${environment.apiHost}/Leg/updateDriverStatus/${isUpdatedBySystem}`;

    return this.httpClient.post(url, status).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  vehicleStatusCategory(): Observable<any> {
    const url = environment.apiHost + '/AppCategory/vehicleStatusCategory';

    return this.httpClient.get(url).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  aircraftStatusCategory(): Observable<any> {
    const url = environment.apiHost + '/AppCategory/aircraftStatusCategory';

    return this.httpClient.get(url).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getAllPendingRequestList(page, selectedPage, searchText): Observable<any> {
    // todo: current user details should be set via function parameters
    const user: ILoggedInUserViewModel = JSON.parse(localStorage.getItem('user'));

    const url =
      environment.apiHost +
      '/Leg/getAllAssignedLeg/' +
      user['role'] +
      '/' +
      user['memberId'] +
      '/' +
      page +
      '/' +
      selectedPage +
      '/' +
      searchText;

    return this.httpClient.get<IDriverLegRequestViewModel>(url).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getAllDriverCompletedList(page, selectedPage, searchText): Observable<any> {
    // todo: current user details should be set via function parameters
    const user: ILoggedInUserViewModel = JSON.parse(localStorage.getItem('user'));
    const url =
      environment.apiHost +
      '/Leg/getAllAssignedCompletedLeg/' +
      user['role'] +
      '/' +
      user['memberId'] +
      '/' +
      page +
      '/' +
      selectedPage +
      '/' +
      searchText;

    return this.httpClient.get<IDriverLegRequestViewModel>(url).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  addMilesInLeg(id, data): Observable<any> {
    const url = environment.apiHost + '/Leg/' + id;

    return this.httpClient.post(url, { Miles: data }).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  updatedLeg(legPayload: LegPayload, legId): Observable<LegPayload> {
    const url = environment.apiHost + '/Leg/' + legId;

    return this.httpClient.put<LegPayload>(url, legPayload).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getCurrentLegLocation(legId: string): Observable<Coords> {
    const url = environment.apiHost + '/Leg/getLocation/' + legId;

    return this.httpClient.get<Coords>(url).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  updateCurrentLegLocation(legLocationPayload, legId): Observable<any> {
    const url = environment.apiHost + '/Leg/updateLocation/' + legId;

    return this.httpClient.put(url, legLocationPayload).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  updateAssignLeg(assignmentId: string, legAssignmentDetail): Observable<any> {
    const url = environment.apiHost + '/Leg/editLegAssignment/' + assignmentId;

    return this.httpClient.put(url, legAssignmentDetail).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  cancelAssignment(id: string): Observable<any> {
    const url = environment.apiHost + '/Leg/cancelAssignment/' + id;

    return this.httpClient.get(url).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getRepositionDuration(body: RepositionDurationBodyInterface): Observable<RepositionDurationResponse> {
    const url = `${environment.apiHostV2}/air-leg-calculator/reposition-duration`;

    return this.httpClient.post<RepositionDurationResponse>(url, body).pipe(
      map((response) => normalizeRepositionDurationResponse(response)),
      catchError((error: HttpErrorResponse) => throwError(() => error)),
    );
  }

  // todo typo mistake, will be fixed later
  getlegAssignmentDetail(id: string): Observable<AssignedLegDetails> {
    const url = environment.apiHost + '/Leg/AssignLegDetails/' + id;

    return this.httpClient.get<AssignedLegDetails>(url).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  async GetOriginAndDestinationLocationByLegIdList(legIdList: string[]): Promise<IAddressDetailViewModel[]> {
    // todo need to refactor using Promise; it should be Observable
    return await new Promise<IAddressDetailViewModel[]>((resolve) => {
      this._locationService.GetLocationByLegIdList(legIdList).subscribe(
        (response) => {
          resolve(response);
        },
        (error) => {
          resolve([]);
        },
      );
    });
  }

  getMultipleAssignedLegDetailList(editAssignmentDetsils: IEditAssignmentPayload): Observable<IEditAssignmentDetailViewModel> {
    const url = `${environment.apiHost}/Leg/EditAssignmentDetailViewModel`;
    return this.httpClient
      .post<IEditAssignmentDetailViewModel>(url, editAssignmentDetsils)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }
}
